/**
 * Utility for persisting search params across navigation.
 *
 * This allows breadcrumb navigation to preserve the user's filter state
 * when navigating back to a list page, without relying on browser history.
 *
 * Uses sessionStorage to:
 * - Survive page refreshes within a session
 * - Clear automatically when the tab is closed
 */

const STORAGE_KEY_PREFIX = "estabrook_search_params_";

/**
 * Saves search params for a given route to sessionStorage.
 *
 * @param route - The route path (e.g., "/customer", "/driver-management")
 * @param params - The URLSearchParams or a plain object of params
 */
export const saveSearchParams = (
  route: string,
  params: URLSearchParams | Record<string, string>,
): void => {
  try {
    const paramsString =
      params instanceof URLSearchParams
        ? params.toString()
        : new URLSearchParams(params).toString();

    // Only save if there are actually params to save
    if (paramsString) {
      sessionStorage.setItem(`${STORAGE_KEY_PREFIX}${route}`, paramsString);
    } else {
      // Clear stored params if empty
      sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${route}`);
    }
  } catch (error) {
    // Silently fail if sessionStorage is not available
    console.warn("Failed to save search params to sessionStorage:", error);
  }
};

/**
 * Retrieves stored search params for a given route.
 *
 * @param route - The route path (e.g., "/customer", "/driver-management")
 * @returns The stored search params string (without leading "?") or empty string
 */
export const getStoredSearchParams = (route: string): string => {
  try {
    return sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${route}`) || "";
  } catch (error) {
    // Silently fail if sessionStorage is not available
    console.warn("Failed to get search params from sessionStorage:", error);
    return "";
  }
};

/**
 * Builds a URL with persisted search params for breadcrumb navigation.
 *
 * @param route - The route path (e.g., "/customer", "/driver-management")
 * @returns The route with appended search params if they exist
 */
export const buildUrlWithPersistedParams = (route: string): string => {
  const storedParams = getStoredSearchParams(route);
  if (storedParams) {
    return `${route}?${storedParams}`;
  }
  return route;
};

/**
 * Clears stored search params for a given route.
 *
 * @param route - The route path to clear params for
 */
export const clearStoredSearchParams = (route: string): void => {
  try {
    sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${route}`);
  } catch (error) {
    console.warn("Failed to clear search params from sessionStorage:", error);
  }
};

/**
 * Clears all stored search params.
 */
export const clearAllStoredSearchParams = (): void => {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(STORAGE_KEY_PREFIX))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    console.warn(
      "Failed to clear all search params from sessionStorage:",
      error,
    );
  }
};
